import React from 'react';
import { MapPin, Navigation, Clock, Phone } from 'lucide-react';
import { locations, salonInfo } from '../data/mock';

export const LocationsSection = () => {
  const openWhatsApp = (locationName) => {
    const message = `¡Hola! Me gustaría obtener más información sobre la sucursal ${locationName}.`;
    const whatsappUrl = `https://wa.me/${salonInfo.whatsapp.replace(/\+/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section id="locations" className="relative overflow-hidden bg-neutral-100 py-24">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #d4af37 0.5px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#d4af37]/10 px-4 py-1.5 text-sm font-medium text-[#d4af37] border border-[#d4af37]/20">
            <MapPin className="h-4 w-4" />
            Ubicaciones
          </div>
          <h2 className="font-serif text-3xl font-bold text-neutral-800 sm:text-4xl lg:text-5xl">
            Encuéntranos
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-neutral-600">
            Visita cualquiera de nuestras sucursales para vivir una experiencia única de belleza y relajación
          </p>
        </div>

        {/* Locations Grid */}
        <div className="mt-16 grid gap-8 lg:grid-cols-2">
          {locations.map((location) => (
            <div
              key={location.id}
              className={`group relative overflow-hidden rounded-3xl bg-white shadow-lg transition-all duration-300 hover:shadow-xl border border-neutral-200 ${
                !location.available ? 'opacity-90' : ''
              }`}
            >
              {/* Badge for coming soon */}
              {!location.available && (
                <div className="absolute right-4 top-4 z-10 rounded-full bg-[#d4af37] px-4 py-1 text-sm font-semibold text-neutral-900">
                  Próximamente
                </div>
              )}

              {/* Map or Placeholder */}
              <div className="relative h-64 w-full overflow-hidden bg-neutral-200">
                {location.available && location.mapUrl ? (
                  <iframe
                    src={location.mapUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`Mapa de ${location.name}`}
                    className="grayscale transition-all duration-300 group-hover:grayscale-0"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center bg-gradient-to-br from-neutral-800 to-neutral-900">
                    <div className="text-center">
                      <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full border-2 border-[#d4af37]/30">
                        <MapPin className="h-10 w-10 text-[#d4af37]" />
                      </div>
                      <p className="text-lg font-medium text-white">
                        ¡Muy pronto aquí!
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Overlay gradient */}
                <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent" />
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-neutral-800">
                  {location.name}
                </h3>
                
                <div className="mt-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#d4af37]" />
                    <div>
                      <p className="text-neutral-700">{location.address}</p>
                      <p className="text-sm text-neutral-500">{location.colony}</p>
                    </div>
                  </div>

                  {location.available && (
                    <>
                      <div className="flex items-center gap-3">
                        <Clock className="h-5 w-5 flex-shrink-0 text-[#d4af37]" />
                        <p className="text-neutral-600">Lun - Sáb: 9:00 AM - 8:00 PM</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Phone className="h-5 w-5 flex-shrink-0 text-[#d4af37]" />
                        <p className="text-neutral-600">{salonInfo.whatsappFormatted}</p>
                      </div>
                    </>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="mt-6 flex gap-3">
                  {location.available ? (
                    <>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.address + ', ' + location.colony)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-neutral-100 py-3 font-medium text-neutral-700 transition-colors hover:bg-neutral-200 border border-neutral-200"
                      >
                        <Navigation className="h-4 w-4" />
                        Cómo llegar
                      </a>
                      <button
                        onClick={() => openWhatsApp(location.name)}
                        className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#d4af37] to-[#b8860b] py-3 font-medium text-neutral-900 transition-all hover:shadow-lg"
                      >
                        <Phone className="h-4 w-4" />
                        Contactar
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => openWhatsApp(location.name)}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#d4af37] to-[#b8860b] py-3 font-medium text-neutral-900 transition-all hover:shadow-lg"
                    >
                      <Phone className="h-4 w-4" />
                      Recibir notificación de apertura
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
